import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import DeleteModal from "../../Components/Common/DeleteModal";
import {
  Button,
  Col,
  Card,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  CardBody,
  Container,
  CardHeader
} from "reactstrap";
import TableContainer from "../../Components/Common/TableContainer";

import {
  addRol,
  deleteRol,
  getRol,
  updateRol,
} from "../../slices/roles/thunks";
import { useDispatch, useSelector } from "react-redux";
import CommonBreadCrumb from "../../Components/Common/BreadCrumb";

const Spinner = ({ isLoading }: { isLoading: boolean }) => (
  isLoading ? (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
      }}
    >
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  ) : null
)

const RolList = () => {
  const dispatch: any = useDispatch();
  const [roles, setRoles] = useState<any>([]);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [modal, setModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    dispatch(getRol());
  }, [dispatch]);
  const rol = useSelector((state: any) => state.Roles.rolLists);

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setRoles(null);
    } else {
      setModal(true);
    }
  }, [modal]);

  const onClickDelete = (rol: any) => {
    setRoles(rol);
    setDeleteModal(true);
  };

  const handleDeleteTask = async () => {
    setIsLoading(true)
    if (roles) {
      await dispatch(deleteRol(roles.id));
      setDeleteModal(false);
      validation.resetForm();
      setIsLoading(false)
    }
  };

  const validation: any = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: (roles && roles.name) || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Por favor ingrese el nombre del rol"),
    }),
    onSubmit: async (values) => {
      setIsLoading(true)
      if (isEdit) {
        const updatedForm = {
          id: roles.id,
          name: values.name,
        };
        dispatch(updateRol(updatedForm));
        validation.resetForm();
      } else {
        const newRol = {
          name: values["name"],
        };
        await dispatch(addRol(newRol));
        validation.resetForm();
      }
      toggle();
      setIsLoading(false)
    },
  });

  const handleCustomerClick = useCallback(
    (arg: any) => {
      const roles = arg;

      setRoles({
        id: roles.id,
        name: roles.name,
      });
      setIsEdit(true);
      toggle();
    },
    [toggle]
  );

  const columns = useMemo(
    () => [
      {
        header: "Nombre",
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: "Descripción",
        accessorKey: "description",
        enableColumnFilter: false,
      },

      {
        header: "Acciónes",
        enableColumnFilter: false,
        cell: (cell: any) => {
            document.title = "Roles y permisos | Vuelo";
          return (
            <React.Fragment>
              <div className="d-flex flex-row-reverse">
                <div className="flex-grow-1 tasks_name"></div>
                <div className="flex-shrink-0 ms-4">
                  <ul className="list-inline">
                      <div className="d-flex gap-2">
                          <Button
                              color="warning"
                              size="sm"
                              title="Editar"
                              onClick={() => {
                                  const rolData = cell.row.original;
                                  handleCustomerClick(rolData);
                              }}
                          >
                              <i className="ri-pencil-line"></i>
                          </Button>

                          <Button
                              color="danger"
                              size="sm"
                              title="Eliminar"
                              onClick={() => {
                                  const rolData = cell.row.original;
                                  onClickDelete(rolData);
                              }}
                          >
                              <i className="ri-delete-bin-line"></i>
                          </Button>

                      </div>
                  </ul>
                </div>
              </div>
            </React.Fragment>
          );
        },
      },
    ],
    [handleCustomerClick]
  );

  return (
    <React.Fragment>
      <Spinner isLoading={isLoading}/>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteTask}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <Container fluid>
            <CommonBreadCrumb title="Roles y permisos" pageTitle="Inicio" />
        <Row>
          <Col>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Roles</h5>
                  <Button color="primary" onClick={() => {
                          setIsEdit(false);
                          validation.resetForm();
                          toggle();
                        }} 
                        disabled={isLoading}>
                    <i className="ri-add-line align-bottom me-1"></i> Agregar Rol
                  </Button>
              </CardHeader>
              <CardBody>
                    <TableContainer
                        columns={columns}
                        data={rol || []}
                        isGlobalFilter={true}
                        customPageSize={10}
                        theadClass="text-muted text-uppercase"
                        SearchPlaceholder="Buscar por nombre, correo o rol..."
                        divClass="table-responsive table-card mb-1"
                        tableClass="align-middle table-nowrap"
                    />
              </CardBody>
            </Card>
          </Col>
        </Row>
        </Container>
      </div>

      <Modal
        isOpen={modal}
        toggle={toggle}
        centered
        size="sm"
        className="border-0"
        modalClassName="modal fade zoomIn"
        backdrop="static"
      >
        <ModalHeader className="p-3 bg-info-subtle" toggle={toggle}>
          {isEdit ? "Editar Rol" : "Crear Rol"}
        </ModalHeader>
        <Form
          className="tablelist-form"
          onSubmit={(e: any) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <ModalBody className="modal-body">
            <Row className="g-3">
              <Col lg={12}>
                <Label for="name" className="form-label">
                  Nombre
                </Label>
                <Input
                  name="name"
                  id="name"
                  className="form-control"
                  placeholder="Ingrese el nombre del rol"
                  type="text"
                  validate={{
                    required: { value: true },
                  }}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.name || ""}
                  invalid={
                    !!(validation.touched.name && validation.errors.name)
                  }
                />
                {validation.touched.name && validation.errors.name ? (
                  <FormFeedback type="invalid">
                    {validation.errors.name}
                  </FormFeedback>
                ) : null}
              </Col>
            </Row>
          </ModalBody>
          <div className="modal-footer">
            <div className="hstack gap-2 justify-content-end">
              <Button
                type="button"
                onClick={() => {
                  setModal(false);
             
                  setRoles(null);
                }}
                className="btn-light"
              >
                Cerrar
              </Button>
              <button type="submit" className="btn btn-primary" id="add-btn">
                {isEdit ? "Actualizar" : "Agregar"}
              </button>
            </div>
          </div>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
export default RolList;
